import { memo, useCallback, useState, useMemo, useEffect, useRef } from 'react'
import { motion, AnimatePresence, Variants } from 'framer-motion'
import { AnimatedLabel, ResizableDiv } from '../../../components'
import { CircleUser } from 'lucide-react'
import { Button, Tooltip } from '@highlight/ui'

const memoryButtonVariants = {
  initial: {
    boxShadow: '0 0 0px rgba(98, 196, 98, 0)',
  },
  animate: {
    boxShadow: '0 0 12px rgba(98, 196, 98, 0.7)',
    transition: {
      duration: 1,
      ease: 'easeInOut',
    },
  },
  exit: {
    boxShadow: '0 0 0px rgba(98, 196, 98, 0)',
    transition: {
      duration: 1,
      ease: 'easeInOut',
    },
  },
} as Variants

export const MemoryButton = memo(({ fact, autoUpdated = false }: { fact: string; autoUpdated?: boolean }) => {
  const [hasShimmered, setHasShimmered] = useState(false)
  const mountedRef = useRef(true)

  // On mount
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  useEffect(() => {
    if (autoUpdated && !hasShimmered) {
      const timer = setTimeout(() => {
        if (mountedRef.current) {
          setHasShimmered(true)
        }
      }, 2500)
      return () => clearTimeout(timer)
    }
  }, [autoUpdated, hasShimmered])

  const handleEditMemory = useCallback(() => {
    console.log('[MemoryButton] User clicked to edit memory')
    // Navigate to memories page using deeplink
    window.open('highlight://settings/account', '_blank')
    window.ampltd.trackEvent('Chat Message', {
      action: 'Edit Memory',
    })
  }, [])

  const memoryButtonTooltipText = useMemo(
    () => (
      <div className="flex max-w-[300px] flex-col gap-1.5 p-0.5">
        <span>Edit this memory</span>
        <span className="text-light/50 whitespace-normal font-normal">{fact}</span>
      </div>
    ),
    [fact],
  )

  return (
    <Tooltip>
      <Tooltip.Trigger render={<div className="relative inline-block" />}>
        {autoUpdated && !hasShimmered && (
          <>
            <motion.div
              className="pointer-events-none absolute inset-0 rounded-full"
              initial="initial"
              animate={['animate', 'exit']}
              variants={memoryButtonVariants}
            />
            <motion.div
              className="pointer-events-none absolute inset-0 rounded-full border-2 border-transparent"
              initial={{ borderColor: 'rgba(98, 196, 98, 0)' }}
              animate={{
                borderColor: ['rgba(98, 196, 98, 0)', 'rgba(98, 196, 98, 0.7)', 'rgba(98, 196, 98, 0)'],
                transition: {
                  duration: 2,
                  times: [0, 0.5, 1],
                  ease: 'easeInOut',
                },
              }}
            />
          </>
        )}
        <Button
          variant="ghost"
          size="sm"
          shape="rounded"
          onClick={handleEditMemory}
          className="relative w-fit gap-1 pl-1 pr-3 opacity-100 hover:opacity-70"
        >
          <CircleUser size={22} className="size-[22px]" />
          <ResizableDiv className="whitespace-nowrap">
            <AnimatePresence mode="wait">
              <motion.div
                key="edit-memory"
                initial={{ opacity: 0.6, y: 5 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -5 }}
                transition={{ duration: 0.2 }}
              >
                <AnimatedLabel
                  label={
                    <motion.span
                      key="memory-detected-label"
                      initial={{ color: 'rgb(98, 196, 98)' }}
                      animate={
                        hasShimmered
                          ? { color: 'rgb(153, 153, 153)' }
                          : {
                              color: ['rgb(98, 196, 98)', 'rgb(98, 196, 98)', 'rgb(153, 153, 153)'],
                              transition: {
                                times: [0, 0.7, 1],
                                duration: 2.5,
                                ease: 'easeInOut',
                              },
                            }
                      }
                    >
                      Edit Facts
                    </motion.span>
                  }
                  transitionMs={250}
                />
              </motion.div>
            </AnimatePresence>
          </ResizableDiv>
          <div className="bg-green absolute left-[16px] top-[15px] flex h-2.5 w-2.5 items-center justify-center rounded-full p-0.5 text-black outline outline-2 outline-[#323232] transition-all duration-300 [&_svg]:opacity-100">
            <svg
              width="100%"
              height="100%"
              viewBox="0 0 10 8"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="transition-opacity duration-300"
            >
              <path
                d="M0.75 4.00004L3.58 6.83004L9.25 1.17004"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        </Button>
      </Tooltip.Trigger>
      <Tooltip.Content>{memoryButtonTooltipText}</Tooltip.Content>
    </Tooltip>
  )
})

MemoryButton.displayName = 'MemoryButton'
